import React, { useState } from 'react';
import { PlusIcon, EditIcon, TrashIcon, CopyIcon, CheckIcon, XIcon, PlayIcon, HistoryIcon, GitBranchIcon, SaveIcon, EyeIcon, CodeIcon, RefreshCwIcon, TagIcon, ToggleLeftIcon, ToggleRightIcon, DollarSignIcon, LinkIcon, LayersIcon, AlertTriangleIcon, CheckCircleIcon } from 'lucide-react';
import { StatusBadge } from '../StatusBadge';
import { RuleSetEditor } from './rules/RuleSetEditor';
import { TestRunner } from './rules/TestRunner';
import { OrgUnitSelector } from '../settings/orgunit/OrgUnitSelector';
import type { OrgUnit } from './sharedFinancialData';
type RuleSet = {
  id: string;
  name: string;
  description: string;
  module: string;
  triggerEvent: string;
  status: 'Active' | 'Draft' | 'Inactive';
  scope: 'Global' | 'OrgUnit';
  orgUnitId?: string;
  orgUnitName?: string;
  orgUnitPath?: string;
  lastModified: string;
  version: string;
  ruleType?: string;
  documentTypes?: string[];
  execution?: string;
  parameters?: any[];
  conditions: any[];
  actions: any[];
  linkedRules?: any[];
  jsonOutput?: string;
};
export function RulesManager({
  locale
}) {
  const [activeView, setActiveView] = useState('overview');
  const [selectedRuleSet, setSelectedRuleSet] = useState<RuleSet | null>(null);
  const [showTestRunner, setShowTestRunner] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Sample data for rule sets
  const [ruleSets, setRuleSets] = useState<RuleSet[]>([{
    id: 'rule-001',
    name: 'Invoice Approval Workflow',
    description: 'Determines the approval path for invoices based on amount, vendor, and GL code',
    module: 'AP',
    triggerEvent: 'Invoice Submitted',
    status: 'Active',
    scope: 'Global',
    lastModified: '2023-10-15T14:30:00Z',
    version: '1.2',
    conditions: [{
      id: 'cond-001',
      type: 'group',
      operator: 'AND',
      children: [{
        id: 'cond-002',
        type: 'condition',
        field: 'Value',
        operator: 'greaterThan',
        value: '10000'
      }, {
        id: 'cond-003',
        type: 'condition',
        field: 'GL Code',
        operator: 'equals',
        value: '5000.5200'
      }]
    }],
    actions: [{
      id: 'action-001',
      type: 'route',
      target: 'Finance Manager',
      targetType: 'role'
    }, {
      id: 'action-002',
      type: 'notify',
      target: 'john.smith@example.com',
      targetType: 'email',
      template: 'High Value Invoice'
    }]
  }, {
    id: 'rule-002',
    name: 'PO Auto-Approval',
    description: 'Automatically approves purchase orders under certain conditions',
    module: 'Procurement',
    triggerEvent: 'PO Created',
    status: 'Draft',
    scope: 'Global',
    lastModified: '2023-10-10T09:15:00Z',
    version: '0.5',
    conditions: [{
      id: 'cond-004',
      type: 'group',
      operator: 'OR',
      children: [{
        id: 'cond-005',
        type: 'condition',
        field: 'Value',
        operator: 'lessThan',
        value: '1000'
      }, {
        id: 'cond-006',
        type: 'condition',
        field: 'Vendor',
        operator: 'in',
        value: ['vendor-001', 'vendor-002']
      }]
    }],
    actions: [{
      id: 'action-003',
      type: 'auto-approve'
    }]
  }, {
    id: 'rule-003',
    name: 'Singapore High-Value Approval',
    description: 'Special approval workflow for high-value transactions in Singapore office',
    module: 'AP',
    triggerEvent: 'Invoice Submitted',
    status: 'Active',
    scope: 'OrgUnit',
    orgUnitId: 'ou-apac-sg',
    orgUnitName: 'Singapore Office',
    orgUnitPath: 'Global Technologies Inc. > APAC Region > Singapore Office',
    lastModified: '2023-11-01T10:15:00Z',
    version: '1.0',
    conditions: [{
      id: 'cond-sg-001',
      type: 'condition',
      field: 'Value',
      operator: 'greaterThan',
      value: '50000'
    }],
    actions: [{
      id: 'action-sg-001',
      type: 'route',
      target: 'Regional Director',
      targetType: 'role'
    }, {
      id: 'action-sg-002',
      type: 'notify',
      target: 'compliance@sg.example.com',
      targetType: 'email'
    }]
  }, {
    id: 'rule-004',
    name: 'Mexico Vendor Compliance',
    description: 'Compliance checks for vendors in Mexico operations',
    module: 'Vendor',
    triggerEvent: 'Vendor Created',
    status: 'Active',
    scope: 'OrgUnit',
    orgUnitId: 'ou-latam-mx',
    orgUnitName: 'Mexico Office',
    orgUnitPath: 'Global Technologies Inc. > LATAM Region > Mexico Office',
    lastModified: '2023-10-28T16:20:00Z',
    version: '1.1',
    conditions: [{
      id: 'cond-mx-001',
      type: 'condition',
      field: 'Country',
      operator: 'equals',
      value: 'MX'
    }],
    actions: [{
      id: 'action-mx-001',
      type: 'trigger-workflow',
      workflowId: 'mx-compliance'
    }, {
      id: 'action-mx-002',
      type: 'notify',
      target: 'compliance-mx',
      targetType: 'group'
    }]
  }]);
  // Sample data for available modules and triggers
  const modules = [{
    id: 'AP',
    name: 'Accounts Payable'
  }, {
    id: 'Procurement',
    name: 'Procurement'
  }, {
    id: 'Vendor',
    name: 'Vendor Management'
  }, {
    id: 'GL',
    name: 'General Ledger'
  }, {
    id: 'Expense',
    name: 'Expense Management'
  }, {
    id: 'Financial',
    name: 'Financial Management'
  }];
  const triggerEvents = {
    AP: [{
      id: 'Invoice Submitted',
      name: 'Invoice Submitted'
    }, {
      id: 'Invoice Approved',
      name: 'Invoice Approved'
    }, {
      id: 'Payment Initiated',
      name: 'Payment Initiated'
    }],
    Procurement: [{
      id: 'PO Created',
      name: 'PO Created'
    }, {
      id: 'PO Approved',
      name: 'PO Approved'
    }, {
      id: 'Requisition Submitted',
      name: 'Requisition Submitted'
    }],
    Vendor: [{
      id: 'Vendor Created',
      name: 'Vendor Created'
    }, {
      id: 'Vendor Updated',
      name: 'Vendor Updated'
    }, {
      id: 'Vendor Approved',
      name: 'Vendor Approved'
    }],
    GL: [{
      id: 'Journal Entry Created',
      name: 'Journal Entry Created'
    }, {
      id: 'Period Closing',
      name: 'Period Closing'
    }],
    Expense: [{
      id: 'Expense Report Submitted',
      name: 'Expense Report Submitted'
    }, {
      id: 'Expense Approved',
      name: 'Expense Approved'
    }],
    Financial: [{
      id: 'Document Submitted',
      name: 'Document Submitted'
    }, {
      id: 'Document Approved',
      name: 'Document Approved'
    }, {
      id: 'Document Rejected',
      name: 'Document Rejected'
    }]
  };
  // Sample data for reusable conditions
  const [reusableConditions, setReusableConditions] = useState([{
    id: 'template-001',
    name: 'High Value Transaction',
    description: 'Identifies transactions above $10,000',
    condition: {
      id: 'cond-temp-001',
      type: 'condition',
      field: 'Value',
      operator: 'greaterThan',
      value: '10000'
    }
  }, {
    id: 'template-002',
    name: 'High Risk Country',
    description: 'Identifies transactions from high risk countries',
    condition: {
      id: 'cond-temp-002',
      type: 'condition',
      field: 'Country',
      operator: 'in',
      value: ['RU', 'CN', 'IR', 'KP', 'SY']
    }
  }]);
  // Handler for creating a new rule set
  const handleCreateRuleSet = () => {
    const newRuleSet: RuleSet = {
      id: `rule-${Date.now()}`,
      name: 'New Rule Set',
      description: '',
      module: '',
      triggerEvent: '',
      status: 'Draft',
      scope: 'Global',
      lastModified: new Date().toISOString(),
      version: '0.1',
      conditions: [],
      actions: []
    };
    setSelectedRuleSet(newRuleSet);
    setActiveView('edit');
  };
  // Handler for editing a rule set
  const handleEditRuleSet = (ruleSet: RuleSet) => {
    setSelectedRuleSet(ruleSet);
    setActiveView('edit');
  };
  // Handler for duplicating a rule set
  const handleDuplicateRuleSet = (ruleSet: RuleSet) => {
    const duplicatedRuleSet: RuleSet = {
      ...ruleSet,
      id: `rule-${Date.now()}`,
      name: `Copy of ${ruleSet.name}`,
      status: 'Draft',
      lastModified: new Date().toISOString(),
      version: '0.1'
    };
    setRuleSets([...ruleSets, duplicatedRuleSet]);
  };
  // Handler for activating/deactivating a rule set
  const handleToggleRuleSetStatus = (ruleSetId: string) => {
    setRuleSets(ruleSets.map(rs => rs.id === ruleSetId ? {
      ...rs,
      status: rs.status === 'Active' ? 'Inactive' : rs.status === 'Inactive' ? 'Active' : rs.status,
      lastModified: new Date().toISOString()
    } : rs));
  };
  // Handler for deleting a rule set
  const handleDeleteRuleSet = (ruleSetId: string) => {
    setDeletingId(ruleSetId);
    setShowDeleteConfirm(true);
  };
  const confirmDelete = () => {
    if (deletingId) {
      setRuleSets(ruleSets.filter(rs => rs.id !== deletingId));
      setShowDeleteConfirm(false);
      setDeletingId(null);
    }
  };
  // Handler for testing a rule set
  const handleTestRuleSet = (ruleSet: RuleSet) => {
    setSelectedRuleSet(ruleSet);
    setShowTestRunner(true);
    setActiveView('edit');
  };
  // Handler for saving a rule set
  const handleSaveRuleSet = (updatedRuleSet: RuleSet) => {
    if (updatedRuleSet.id && ruleSets.find(rs => rs.id === updatedRuleSet.id)) {
      // Update existing rule set
      setRuleSets(ruleSets.map(rs => rs.id === updatedRuleSet.id ? {
        ...updatedRuleSet,
        lastModified: new Date().toISOString()
      } : rs));
    } else {
      // Create new rule set
      const newRuleSet: RuleSet = {
        ...updatedRuleSet,
        id: updatedRuleSet.id || `rule-${Date.now()}`,
        lastModified: new Date().toISOString()
      };
      setRuleSets([...ruleSets, newRuleSet]);
    }
    setActiveView('overview');
    setSelectedRuleSet(null);
  };
  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  // Get rule type icon
  const getRuleTypeIcon = (ruleSet: RuleSet) => {
    if (ruleSet.ruleType === 'Financial Delegation') {
      return <DollarSignIcon className="h-4 w-4 text-green-500 mr-2" data-id="element-11631" />;
    } else if (ruleSet.module === 'Vendor') {
      return <TagIcon className="h-4 w-4 text-orange-500 mr-2" data-id="element-11632" />;
    } else {
      return <GitBranchIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-11633" />;
    }
  };
  const globalRuleSets = ruleSets.filter(rs => rs.scope === 'Global');
  const orgUnitRuleSets = ruleSets.filter(rs => rs.scope === 'OrgUnit');
  // Render the Rule Sets overview
  const renderRuleSetsOverview = () => {
    return <div className="space-y-6" data-id="element-11634">
        {/* Global Rule Sets */}
        <div className="bg-white shadow rounded-lg" data-id="element-11635">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-11636">
            <div className="flex justify-between items-center" data-id="element-11637">
              <div data-id="element-11638">
                <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-11639">
                  Global Rule Sets
                </h3>
                <p className="mt-1 text-sm text-gray-500" data-id="element-11640">
                  Global rules available to all organisation units
                </p>
              </div>
              <button onClick={handleCreateRuleSet} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11641">
                <PlusIcon className="h-4 w-4 mr-2" data-id="element-11642" />
                Create Rule Set
              </button>
            </div>
          </div>

          {globalRuleSets.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-11643">
              <p className="text-sm text-gray-500" data-id="element-11644">
                No global rule sets configured.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-11645">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-11646">
                <thead className="bg-gray-50" data-id="element-11647">
                  <tr data-id="element-11648">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11649">
                      Rule Set
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11650">
                      Module / Trigger
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11651">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11652">
                      Version
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11653">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-11654">
                  {globalRuleSets.map(ruleSet => <tr key={ruleSet.id} data-id="element-11655">
                      <td className="px-6 py-4" data-id="element-11656">
                        <div className="flex items-center" data-id="element-11657">
                          {getRuleTypeIcon(ruleSet)}
                          <div data-id="element-11658">
                            <div className="text-sm font-medium text-gray-900" data-id="element-11659">
                              {ruleSet.name}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-11660">
                              {ruleSet.description}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-11661">
                        <div className="text-sm text-gray-900" data-id="element-11662">
                          {ruleSet.module}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-11663">
                          {ruleSet.triggerEvent}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-11664">
                        {ruleSet.status === 'Active' && <StatusBadge status="success" text="Active" data-id="element-11665" />}
                        {ruleSet.status === 'Draft' && <StatusBadge status="info" text="Draft" data-id="element-11666" />}
                        {ruleSet.status === 'Inactive' && <StatusBadge status="error" text="Inactive" data-id="element-11667" />}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-11668">
                        <div className="text-sm text-gray-900" data-id="element-11669">
                          v{ruleSet.version}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-11670">
                        <div className="flex justify-end space-x-2" data-id="element-11671">
                          <button onClick={() => handleEditRuleSet(ruleSet)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-11672">
                            <EditIcon className="h-4 w-4" data-id="element-11673" />
                          </button>
                          <button onClick={() => handleTestRuleSet(ruleSet)} className="text-blue-600 hover:text-blue-900" title="Test" data-id="element-11674">
                            <PlayIcon className="h-4 w-4" data-id="element-11675" />
                          </button>
                          <button onClick={() => handleDuplicateRuleSet(ruleSet)} className="text-green-600 hover:text-green-900" title="Duplicate" data-id="element-11676">
                            <CopyIcon className="h-4 w-4" data-id="element-11677" />
                          </button>
                          <button onClick={() => handleDeleteRuleSet(ruleSet.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-11678">
                            <TrashIcon className="h-4 w-4" data-id="element-11679" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>

        {/* OrgUnit Rule Sets */}
        <div className="bg-white shadow rounded-lg" data-id="element-11680">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-11681">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-11682">
              Organisation Unit Rule Sets
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-11683">
              Rules specific to individual organisation units
            </p>
          </div>

          {orgUnitRuleSets.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-11684">
              <p className="text-sm text-gray-500" data-id="element-11685">
                No organisation unit rule sets configured.
              </p>
            </div> : <div className="overflow-x-auto" data-id="element-11686">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-11687">
                <thead className="bg-gray-50" data-id="element-11688">
                  <tr data-id="element-11689">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11690">
                      Organisation Unit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11691">
                      Rule Set
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11692">
                      Module / Trigger
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11693">
                      Status
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11694">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-11695">
                  {orgUnitRuleSets.map(ruleSet => <tr key={ruleSet.id} data-id="element-11696">
                      <td className="px-6 py-4" data-id="element-11697">
                        <div className="text-sm font-medium text-gray-900" data-id="element-11698">
                          {ruleSet.orgUnitName}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-11699">
                          {ruleSet.orgUnitPath}
                        </div>
                      </td>
                      <td className="px-6 py-4" data-id="element-11700">
                        <div className="flex items-center" data-id="element-11701">
                          {getRuleTypeIcon(ruleSet)}
                          <div data-id="element-11702">
                            <div className="text-sm font-medium text-gray-900" data-id="element-11703">
                              {ruleSet.name}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-11704">
                              {ruleSet.description}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-11705">
                        <div className="text-sm text-gray-900" data-id="element-11706">
                          {ruleSet.module}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-11707">
                          {ruleSet.triggerEvent}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-11708">
                        {ruleSet.status === 'Active' && <StatusBadge status="success" text="Active" data-id="element-11709" />}
                        {ruleSet.status === 'Draft' && <StatusBadge status="info" text="Draft" data-id="element-11710" />}
                        {ruleSet.status === 'Inactive' && <StatusBadge status="error" text="Inactive" data-id="element-11711" />}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-11712">
                        <div className="flex justify-end space-x-2" data-id="element-11713">
                          <button onClick={() => handleEditRuleSet(ruleSet)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-11714">
                            <EditIcon className="h-4 w-4" data-id="element-11715" />
                          </button>
                          <button onClick={() => handleTestRuleSet(ruleSet)} className="text-blue-600 hover:text-blue-900" title="Test" data-id="element-11716">
                            <PlayIcon className="h-4 w-4" data-id="element-11717" />
                          </button>
                          <button onClick={() => handleDuplicateRuleSet(ruleSet)} className="text-green-600 hover:text-green-900" title="Duplicate" data-id="element-11718">
                            <CopyIcon className="h-4 w-4" data-id="element-11719" />
                          </button>
                          <button onClick={() => handleDeleteRuleSet(ruleSet.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-11720">
                            <TrashIcon className="h-4 w-4" data-id="element-11721" />
                          </button>
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>}
        </div>
      </div>;
  };
  return <div className="space-y-6" data-id="element-11722">
      <div className="mb-8" data-id="element-11723">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-11724">Rules</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-11725">
          Define conditional logic for business process automation at global and
          organisation unit levels
        </p>
      </div>

      {/* Main Content */}
      {activeView === 'overview' ? renderRuleSetsOverview() : <div className="space-y-4" data-id="element-11726">
          <div className="flex justify-between items-center mb-4" data-id="element-11727">
            <button onClick={() => {
          setActiveView('overview');
          setSelectedRuleSet(null);
          setShowTestRunner(false);
        }} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-11728">
              ← Back to Rule Sets
            </button>
            <div className="flex space-x-2" data-id="element-11729">
              {!showTestRunner && <button onClick={() => setShowTestRunner(true)} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-11730">
                  <PlayIcon className="h-4 w-4 mr-2" data-id="element-11731" />
                  Test Rule
                </button>}
              {showTestRunner && <button onClick={() => setShowTestRunner(false)} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-11732">
                  <EditIcon className="h-4 w-4 mr-2" data-id="element-11733" />
                  Edit Rule
                </button>}
            </div>
          </div>
          <div className={`grid grid-cols-1 ${showTestRunner ? 'lg:grid-cols-2' : ''} gap-6`} data-id="element-11734">
            {!showTestRunner && <div className="col-span-1" data-id="element-11735">
                <RuleSetEditor ruleSet={selectedRuleSet} modules={modules} triggerEvents={triggerEvents} reusableConditions={reusableConditions} onSave={handleSaveRuleSet} locale={locale} data-id="element-11736" />
              </div>}
            {showTestRunner && <>
                <div className="col-span-1" data-id="element-11737">
                  <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-11738">
                    <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-11739">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-11740">
                        Rule Set Details
                      </h3>
                    </div>
                    <div className="px-4 py-5 sm:p-6" data-id="element-11741">
                      <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2" data-id="element-11742">
                        <div className="sm:col-span-1" data-id="element-11743">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-11744">
                            Name
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-11745">
                            {selectedRuleSet?.name}
                          </dd>
                        </div>
                        <div className="sm:col-span-1" data-id="element-11746">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-11747">
                            Version
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-11748">
                            v{selectedRuleSet?.version}
                          </dd>
                        </div>
                        <div className="sm:col-span-1" data-id="element-11749">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-11750">
                            Module
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-11751">
                            {selectedRuleSet?.module}
                          </dd>
                        </div>
                        <div className="sm:col-span-1" data-id="element-11752">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-11753">
                            Trigger
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-11754">
                            {selectedRuleSet?.triggerEvent}
                          </dd>
                        </div>
                        <div className="sm:col-span-2" data-id="element-11755">
                          <dt className="text-sm font-medium text-gray-500" data-id="element-11756">
                            Description
                          </dt>
                          <dd className="mt-1 text-sm text-gray-900" data-id="element-11757">
                            {selectedRuleSet?.description}
                          </dd>
                        </div>
                      </dl>
                    </div>
                  </div>
                </div>
                <div className="col-span-1" data-id="element-11758">
                  <TestRunner ruleSet={selectedRuleSet} locale={locale} data-id="element-11759" />
                </div>
              </>}
          </div>
        </div>}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-11760">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-11761">
            <div className="fixed inset-0 transition-opacity" data-id="element-11762">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-11763"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" data-id="element-11764">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-11765">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-11766">
                <div className="sm:flex sm:items-start" data-id="element-11767">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-11768">
                    <AlertTriangleIcon className="h-6 w-6 text-red-600" data-id="element-11769" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-11770">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-11771">
                      Delete Rule Set
                    </h3>
                    <div className="mt-2" data-id="element-11772">
                      <p className="text-sm text-gray-500" data-id="element-11773">
                        Are you sure you want to delete this rule set? This
                        action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-11774">
                <button type="button" onClick={confirmDelete} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-11775">
                  Delete
                </button>
                <button type="button" onClick={() => setShowDeleteConfirm(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-11776">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}